import React, {useEffect, useState} from 'react';
import '../styles/TaskList.css';
import useApiStable from "../utils/useApiStable";

function PersonForm({person, onClose}) {
    const [firstName, setFirstName] = useState(person?.firstName || '');
    const [lastName, setLastName] = useState(person?.lastName || '');
    const [email, setEmail] = useState(person?.email || '');
    const [tennis04Id, setTennis04Id] = useState(person?.tennis04Id != null ? String(person.tennis04Id) : '');
    const [gender, setGender] = useState(person?.gender || '');
    const [idNumber, setIdNumber] = useState(person?.idNumber || '');
    const [error, setError] = useState('');
    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState(person?.groups?.map(g => g.id) || []);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [password, setPassword] = useState('');
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const api = useApiStable();

    // Gruppen laden und prüfen, zu welchen die Person gehört
    useEffect(() => {
        api.get('/person-groups')
            .then(data => {
                const allGroups = Array.isArray(data) ? data : [];
                setGroups(allGroups);
                setLoadingGroups(false);
            })
            .catch(err => {
                console.error('Fehler beim Laden der Gruppen:', err);
                setLoadingGroups(false);
            });
    }, []);

    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

    const handleToggleGroup = (groupId) => {
        setSelectedGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const trimmedEmail = email.trim();
        if (!validateEmail(trimmedEmail)) {
            setError('Bitte eine gültige E-Mail-Adresse eingeben.');
            return;
        }

        try {
            const payload = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: trimmedEmail,
                tennis04Id: tennis04Id !== '' ? parseInt(tennis04Id, 10) : null,
                gender: gender || null,
                idNumber: idNumber.trim() || null,
            };
            const request = person
                ? api.put(`/persons/${person.id}`, payload)
                : api.post(`/persons`, payload);

            const savedPerson = await request;
            const personId = savedPerson.id || person?.id;

            // Gruppenzuweisungen aktualisieren
            if (personId) {
                await updateGroupMemberships(personId);
            }

            onClose(true);
        } catch (err) {
            setError(err.message || 'Speichern fehlgeschlagen');
        }
    };

    const updateGroupMemberships = async (personId) => {
        // Finde heraus, welche Gruppen hinzugefügt oder entfernt werden müssen
        const originalGroups = person?.groups
            ? person.groups.map(group => group.id)
            : [];

        console.log('Originalgruppen:', originalGroups);
        console.log('Ausgewählte Gruppen:', selectedGroups);

        const groupsToAdd = selectedGroups.filter(id => !originalGroups.includes(id));
        const groupsToRemove = originalGroups.filter(id => !selectedGroups.includes(id));

        // Füge Person zu neuen Gruppen hinzu
        for (const groupId of groupsToAdd) {
            try {
                await api.post(`/person-groups/${groupId}/members/${personId}`);
            } catch (err) {
                console.error(`Fehler beim Hinzufügen zu Gruppe ${groupId}:`, err);
            }
        }

        // Entferne Person aus Gruppen
        for (const groupId of groupsToRemove) {
            try {
                await api.delete(`/person-groups/${groupId}/members/${personId}`);
            } catch (err) {
                console.error(`Fehler beim Entfernen aus Gruppe ${groupId}:`, err);
            }
        }
    };

    const handleSetPassword = async () => {
        if (!person?.id) {
            setError('Person muss zuerst gespeichert werden');
            return;
        }

        if (password.length < 6) {
            setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
            return;
        }

        setError('');
        setPasswordSuccess('');

        try {
            const response = await api.fetchRaw(`/persons/${person.id}/set-password`, {
                method: 'POST',
                body: JSON.stringify({ password: password })
            });

            const result = await response.json();

            if (response.ok) {
                setPasswordSuccess('Passwort erfolgreich gesetzt!');
                setPassword('');
                setTimeout(() => setPasswordSuccess(''), 3000);
            } else {
                setError(result.error || result.message || 'Passwort setzen fehlgeschlagen');
            }
        } catch (err) {
            console.error('Fehler beim Passwort setzen:', err);
            setError(err.message || 'Passwort setzen fehlgeschlagen');
        }
    };

    return (
        <div className="taskform-card">
            <h3>{person ? 'Person bearbeiten' : 'Neue Person anlegen'}</h3>
            <form onSubmit={handleSubmit} noValidate>
                <div className="taskform-group">
                    <label htmlFor="firstName">Vorname*</label>
                    <input id="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} required/>
                </div>
                <div className="taskform-group">
                    <label htmlFor="lastName">Nachname*</label>
                    <input id="lastName" value={lastName} onChange={e => setLastName(e.target.value)} required/>
                </div>
                <div className="taskform-group">
                    <label htmlFor="email">E-Mail*</label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        className={error && !validateEmail(email) ? 'taskform-input-error' : ''}
                    />
                </div>
                <div className="taskform-group">
                    <label htmlFor="gender">Geschlecht</label>
                    <select id="gender" value={gender} onChange={e => setGender(e.target.value)}>
                        <option value="">— nicht ausgewählt —</option>
                        <option value="MALE">Männlich</option>
                        <option value="FEMALE">Weiblich</option>
                        <option value="NOT_SELECTED">Keine Angabe</option>
                    </select>
                </div>
                <div className="taskform-group">
                    <label htmlFor="idNumber">ID-Nummer (TNB)</label>
                    <input
                        id="idNumber"
                        type="text"
                        value={idNumber}
                        onChange={e => setIdNumber(e.target.value)}
                        placeholder="z.B. 12345"
                    />
                </div>
                <div className="taskform-group">
                    <label htmlFor="tennis04Id">Tennis04-ID</label>
                    <input
                        id="tennis04Id"
                        type="number"
                        value={tennis04Id}
                        onChange={e => setTennis04Id(e.target.value)}
                        min="0"
                        step="1"
                    />
                </div>
                <div className="taskform-group">
                    <label>Gruppen</label>
                    {loadingGroups ? (
                        <p style={{fontSize: '0.9em', color: '#666'}}>Gruppen werden geladen...</p>
                    ) : groups.length === 0 ? (
                        <p style={{fontSize: '0.9em', color: '#666'}}>Keine Gruppen verfügbar</p>
                    ) : (
                        <div style={{
                            maxHeight: '150px',
                            overflowY: 'auto',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            backgroundColor: '#fafafa'
                        }}>
                            {groups.map(group => (
                                <label
                                    key={group.id}
                                    style={{
                                        display: 'block',
                                        padding: '4px 0',
                                        cursor: 'pointer',
                                        fontSize: '0.9em'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedGroups.includes(group.id)}
                                        onChange={() => handleToggleGroup(group.id)}
                                        style={{marginRight: '8px'}}
                                    />
                                    {group.name}
                                    {group.description && (
                                        <span style={{color: '#888', fontSize: '0.9em', marginLeft: '4px'}}>
                      ({group.description})
                    </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Passwort-Verwaltung (nur für bestehende Personen) */}
                {person?.id && (
                    <div className="taskform-group" style={{ borderTop: '1px solid #ddd', paddingTop: '16px', marginTop: '16px' }}>
                        <button
                            type="button"
                            onClick={() => setShowPasswordSection(!showPasswordSection)}
                            style={{
                                background: '#FF9800',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                                marginBottom: showPasswordSection ? '12px' : '0'
                            }}
                        >
                            {showPasswordSection ? '🔒 Passwort-Bereich schließen' : '🔑 Passwort setzen/ändern'}
                        </button>

                        {showPasswordSection && (
                            <div style={{ marginTop: '12px', padding: '12px', background: '#fff3e0', borderRadius: '4px' }}>
                                <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                                    Neues Passwort (mind. 6 Zeichen)
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    minLength={6}
                                    placeholder="Neues Passwort eingeben"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        marginBottom: '8px'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleSetPassword}
                                    disabled={password.length < 6}
                                    style={{
                                        background: password.length >= 6 ? '#4CAF50' : '#ccc',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: password.length >= 6 ? 'pointer' : 'not-allowed',
                                        fontSize: '0.9em'
                                    }}
                                >
                                    Passwort setzen
                                </button>
                                {passwordSuccess && (
                                    <div style={{
                                        color: '#2e7d32',
                                        fontSize: '0.85em',
                                        marginTop: '8px',
                                        padding: '8px',
                                        background: '#e8f5e9',
                                        borderRadius: '4px'
                                    }}>
                                        ✓ {passwordSuccess}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div style={{
                        color: '#c62828',
                        fontSize: '0.8rem',
                        marginTop: '-4px',
                        marginBottom: '8px'
                    }}>{error}</div>
                )}
                <div className="taskform-actions">
                    <button className="taskform-save-btn" type="submit"
                            disabled={!firstName.trim() || !lastName.trim() || !email.trim() || !validateEmail(email)}>Speichern
                    </button>
                    <button className="taskform-cancel-btn" type="button" onClick={() => onClose(false)}>Abbrechen
                    </button>
                </div>
            </form>
        </div>
    );
}

export default PersonForm;

